import React, { forwardRef, Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { clearSuchvorschlaege } from '../../../../redux/actions/adresse';
import Validationtip from '../../../validationtip/Validationtip';
import { ENTER } from '../../../../utils/constants';

interface TableCellTextProps {
  name: string;
  value: string;
  tableName?: string;
  onChangeHandler: any;
  onFocusHandler?: any;
  onClickHandler?: any;
  rowIndex?: number;
  tabIndex: number;
  onBlurHandler: any;
  disabled: boolean;
  selectByAllTablecell?: boolean;
  autocompleteField?: any;
  autocompleteFormat?: any;
  autocompleteHandler?: any;
  suchvorschlaege?: any;
  clearSuchvorschlaege?: any;
  required?: boolean;
  hierarchicalAttributename?: string;
  searchQueryString?: string;
}

/**
 * Komponente für jede einzelene Zelle einer {@link Table.jsx}
 *
 * @param name - Html Name des Elementes
 * @param value Html Value des Elementes
 * @param changeHandler  on change handler
 * @param onFocusHandler  on focus handler
 * @param onClickHandler on click handler
 * @param rowIndex Zeilenindex
 * @param tabIndex tab index (auch -1 or 0), definiert die Tabreihenfolge
 * @param onBlurHandler on blur handler
 * @param disabled
 * @returns {JSX.Element}
 * @constructor
 */
const TableCellText = forwardRef<HTMLInputElement, TableCellTextProps>(
  (
    {
      name,
      value,
      onChangeHandler,
      onFocusHandler,
      onClickHandler,
      rowIndex,
      tabIndex = 0,
      onBlurHandler,
      disabled,
      autocompleteField,
      autocompleteFormat,
      autocompleteHandler,
      suchvorschlaege,
      clearSuchvorschlaege,
      tableName,
      required = false,
      hierarchicalAttributename,
      searchQueryString,
    },
    ref
  ) => {
    const [showList, setShowList] = useState(false);

    useEffect(() => {
      if (!showList) return;
      window.addEventListener('click', closeList);
      return () => window.removeEventListener('click', closeList);
    }, [showList]);

    const closeList = () => {
      setShowList(false);
    };

    /**
     * Definiert die Aktion, die ausgeführt wird, wenn sich das Element verändert.
     * @see https://developer.mozilla.org/de/docs/Web/API/HTMLElement/change_event
     *
     * @param event Html Event
     */
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        const onChangeValue = event.target.value;
        if (autocompleteFormat) {
          !showList && setShowList(true);
          if (onChangeValue.trim() === '') {
            clearSuchvorschlaege();
          }
        }

        onChangeHandler(
          event.target.name,
          hierarchicalAttributename
            ? { [hierarchicalAttributename]: onChangeValue }
            : onChangeValue,
          rowIndex
        );
      }
    };

    /**
     * OnBlur-Handler.
     * Definiert die Aktion, die ausgeführt wird, nachdem das Element seinen Fokus verloren hat.
     * @param event
     */
    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlurHandler(rowIndex, event.target.name);
    };

    /**
     * OnClick-Handler.
     * Definiert die Aktion, die ausgeführt wird, wenn das Element angeklickt wurde.
     */
    const onClick = () => {
      if (onClickHandler) {
        onClickHandler(rowIndex);
      }
    };

    /**
     * OnFocus-Handler.
     * Definiert die Aktion, die ausgeführt wird, wenn sich das Element im Fokus befindet.
     */
    const onFocus = () => {
      onFocusHandler && onFocusHandler(rowIndex);
      if (autocompleteFormat) {
        setShowList(true);
        clearSuchvorschlaege();
      }
    };

    const onListClick = (suchvorschlag: { [x: string]: string }) => {
      if (autocompleteHandler) {
        autocompleteHandler(suchvorschlag);
      } else {
        onChangeHandler(suchvorschlag[autocompleteField]);
      }
      setShowList(false);
      clearSuchvorschlaege();
    };

    const handleKeyDown = (
      e: React.KeyboardEvent,
      suchvorschlag: { [x: string]: string }
    ) => {
      if (e.key === ENTER) {
        onListClick(suchvorschlag);
      }
    };

    /**
     * wenn in der Query ein Suchstring eingegeben wurde, wird dieser in der Tabelle farblich markiert
     * @param val Zelleninhalt
     */
    const getValue = (val: string) => {
      let preSearchQueryString = '';
      let origSearchQueryString = '';
      let afterSearchQueryString = '';
      const suchvorgabe = searchQueryString
        ? searchQueryString.toUpperCase()
        : '_?§$,schlumpf';

      const toFormat = val.toString().toUpperCase().includes(suchvorgabe);

      if (toFormat) {
        const sqsindex = val.toString().toUpperCase().indexOf(suchvorgabe);
        preSearchQueryString = val.toString().substr(0, sqsindex);
        origSearchQueryString = val
          .toString()
          .substr(sqsindex, suchvorgabe.length);
        afterSearchQueryString = val
          .toString()
          .substr(sqsindex + suchvorgabe.length, val.toString().length);
      }
      return (
        <Fragment>
          <input
            tabIndex={tabIndex}
            name={name}
            value={val ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onClick={onClick}
            readOnly={disabled}
            required={required}
            autoComplete={'off'}
            hidden={toFormat}
            ref={ref}
          />
          {toFormat && (
            <div className={'searchQueryFoundContainer'}>
              <label>{preSearchQueryString}</label>
              <label className={'searchQueryFound'}>
                {origSearchQueryString}
              </label>
              <label>{afterSearchQueryString}</label>
            </div>
          )}
        </Fragment>
      );
    };

    return (
      <Fragment>
        <Validationtip
          fieldName={name ? tableName + '[' + rowIndex + '].' + name : ''}
        >
          {getValue(value)}
        </Validationtip>
        {autocompleteFormat &&
          showList &&
          suchvorschlaege &&
          suchvorschlaege.length > 0 && (
            <ul className='suchvorschlaege' tabIndex={-1}>
              {suchvorschlaege.map(
                (suchvorschlag: any, index: React.Key | null | undefined) => {
                  return (
                    <li
                      className='lisuchvorschlaege'
                      key={index}
                      onMouseDown={() => onListClick(suchvorschlag)}
                      tabIndex={0}
                      onKeyDown={e => {
                        handleKeyDown(e, suchvorschlag);
                      }}
                    >
                      {' '}
                      {autocompleteFormat
                        ? React.cloneElement(autocompleteFormat, {
                            suchvorschlag: suchvorschlag,
                          })
                        : suchvorschlag[autocompleteField]}{' '}
                    </li>
                  );
                }
              )}
            </ul>
          )}
      </Fragment>
    );
  }
);

TableCellText.displayName = TableCellText.defaultProps?.name ?? 'TableCellText';

export default connect(null, { clearSuchvorschlaege }, null, {
  forwardRef: true,
})(TableCellText);
